/**
 * ZOY-632 — the password rules the Auth service really enforces, evaluated
 * live. Verified against Auth `develop` (ba7659f): every set-password route
 * (first login, recovery, signed-in change) validates the new password with
 * `@MinLength(8) @MaxLength(128)` and refuses one equal to the password it
 * replaces (PASSWORD_UNCHANGED). Nothing else is checked, so nothing else is
 * listed — a rule shown here is a rule the server will apply.
 */

export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

export type RuleState = 'pending' | 'met' | 'unmet';

export interface PasswordRule {
  id: 'length' | 'different';
  state: RuleState;
}

/**
 * @param next      the candidate password as typed
 * @param previous  the password it replaces when this screen knows it (the
 *                  current one, or the temporary one); null when it does not
 *                  (recovery), in which case the rule stays pending and the
 *                  server is the judge.
 */
export function passwordRules(next: string, previous: string | null): PasswordRule[] {
  const length: RuleState =
    next === ''
      ? 'pending'
      : next.length >= PASSWORD_MIN && next.length <= PASSWORD_MAX
        ? 'met'
        : 'unmet';
  const different: RuleState =
    next === '' || previous === null || previous === ''
      ? 'pending'
      : next !== previous
        ? 'met'
        : 'unmet';
  return [
    { id: 'length', state: length },
    { id: 'different', state: different },
  ];
}

/** True when every rule this screen can check is met (a pending one cannot block). */
export const rulesSatisfied = (rules: readonly PasswordRule[]): boolean =>
  rules.every((r) => r.state !== 'unmet') && rules[0]!.state === 'met';
